const { VUE_APP_S3 } = process.env

// Canonical kinds. UI groups the first three as "Scenes".
// Mood is text-only and selected through a standalone node chip, not @ mentions.
export const ELEMENT_KINDS = ['character', 'location', 'prop', 'personality', 'mood']
export const SCENE_ELEMENT_KINDS = ['character', 'location', 'prop']
export const VISUAL_ELEMENT_KINDS = new Set(SCENE_ELEMENT_KINDS)

// Personality-only sub-grouping. Drives the 2-column dropdown in the
// (Phase 2) standalone Personality chip and the Personality editor pane in
// VybyElementsPopup. Anything missing or unrecognized normalizes to 'Other'.
export const PERSONALITY_CATEGORIES = [
  'Designing',
  'Cloning',
  'Clothing Haul',
  'Other',
]
export const DEFAULT_PERSONALITY_CATEGORY = 'Other'
export const PERSONALITY_CATEGORY_LABELS = {
  Designing: 'Scripts',
}

export const MOOD_CATEGORIES = [
  'Designing',
  'Cloning',
  'Clothing Haul',
  'Storyboard',
  'Other',
]
export const DEFAULT_MOOD_CATEGORY = 'Other'

export const MAX_ACTIVE_ELEMENT_IMAGES = 12
export const MAX_ELEMENTS_PER_PROMPT = 4

function normalizeBase(base) {
  return `${base || ''}`.trim().replace(/\/+$/, '')
}

export function toRelativeS3Path(value) {
  const raw = `${value || ''}`.trim()
  if (!raw) return raw
  if (/^https?:\/\//i.test(raw)) {
    const m = raw.match(/\/s3\/(.+)$/i)
    return m && m[1] ? m[1] : raw
  }
  const base = normalizeBase(VUE_APP_S3)
  if (base && raw.startsWith(`${base}/`)) return raw.slice(base.length + 1)
  if (raw.startsWith('/s3/')) return raw.slice(4)
  if (raw.startsWith('/')) return raw.slice(1)
  return raw
}

export function inferFileExtension(file) {
  const name = `${file?.name || ''}`.trim()
  const ext = name.includes('.') ? name.split('.').pop().trim().toLowerCase() : ''
  if (ext) return ext.replace(/[^a-z0-9]/g, '') || 'png'

  const mime = `${file?.type || ''}`.trim().toLowerCase()
  if (mime === 'image/jpeg') return 'jpg'
  if (mime === 'image/png') return 'png'
  if (mime === 'image/webp') return 'webp'
  if (mime === 'image/gif') return 'gif'
  return 'png'
}

export function createElementId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `element-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function normalizeImageEntry(entry) {
  if (typeof entry === 'string') {
    const path = toRelativeS3Path(entry)
    if (!path) return null
    return { path, active: true }
  }
  if (entry && typeof entry === 'object') {
    const path = typeof entry.path === 'string' ? toRelativeS3Path(entry.path) : ''
    if (!path) return null
    return { path, active: entry.active !== false }
  }
  return null
}

export function normalizePersonalityCategory(value) {
  const raw = `${value || ''}`.trim()
  if (!raw) return DEFAULT_PERSONALITY_CATEGORY
  const match = PERSONALITY_CATEGORIES.find(
    (c) => c.toLowerCase() === raw.toLowerCase(),
  )
  return match || DEFAULT_PERSONALITY_CATEGORY
}

export function normalizePersonalityCategories(value) {
  const list = Array.isArray(value) ? value : [value]
  const seen = new Set()
  const categories = list
    .map(normalizePersonalityCategory)
    .filter((category) => {
      if (!category || seen.has(category)) return false
      seen.add(category)
      return true
    })
  return categories.length ? categories : [DEFAULT_PERSONALITY_CATEGORY]
}

export function personalityCategoryLabel(category) {
  const normalized = normalizePersonalityCategory(category)
  return PERSONALITY_CATEGORY_LABELS[normalized] || normalized
}

export function normalizeMoodCategory(value) {
  const raw = `${value || ''}`.trim()
  if (!raw) return DEFAULT_MOOD_CATEGORY
  const match = MOOD_CATEGORIES.find(
    (c) => c.toLowerCase() === raw.toLowerCase(),
  )
  return match || DEFAULT_MOOD_CATEGORY
}

export function normalizeElement(item, index = 0) {
  if (!item || typeof item !== 'object') return null

  const idRaw = `${item.id || ''}`.trim()
  const nameRaw = `${item.name || ''}`.trim()
  const kindRaw = `${item.kind || ''}`.trim().toLowerCase()
  if (!ELEMENT_KINDS.includes(kindRaw)) return null

  const promptRaw = typeof item.prompt === 'string' ? item.prompt : ''
  const thumbRaw =
    kindRaw !== 'mood' && typeof item.thumbnail === 'string'
      ? toRelativeS3Path(item.thumbnail)
      : ''
  const imagesRaw = Array.isArray(item.images) ? item.images : []
  const images =
    kindRaw === 'mood'
      ? []
      : imagesRaw
          .map(normalizeImageEntry)
          .filter((entry) => entry && entry.path)

  // Categories are only meaningful for text-only standalone kinds. Visual kinds
  // get an empty string so the field is always present (cheap normalization)
  // without polluting the visual UIs.
  const category = (() => {
    if (kindRaw === 'personality') return normalizePersonalityCategory(item.category)
    if (kindRaw === 'mood') return normalizeMoodCategory(item.category)
    return ''
  })()

  return {
    id: idRaw || `element-${index + 1}`,
    name: nameRaw || `Element ${index + 1}`,
    kind: kindRaw,
    prompt: promptRaw,
    thumbnail: thumbRaw || '',
    images,
    category,
  }
}

// Returns only the path strings of active images.
// Used by Phase 5 when injecting element images into workflow media slots.
export function getElementActiveImagePaths(element) {
  if (!element || !Array.isArray(element.images)) return []
  return element.images
    .filter((img) => img && img.active !== false && img.path)
    .map((img) => img.path)
}

// UI-layer convenience: cover image path (thumbnail first, then first active image, then first image).
export function getElementCoverPath(element) {
  if (!element) return ''
  const thumb = `${element.thumbnail || ''}`.trim()
  if (thumb) return thumb
  const images = Array.isArray(element.images) ? element.images : []
  const firstActive = images.find((img) => img && img.active !== false && img.path)
  if (firstActive) return firstActive.path
  const firstAny = images.find((img) => img && img.path)
  return firstAny ? firstAny.path : ''
}

// ---------------------------------------------------------------------------
// Prompt token utilities
//
// Tokens embed references to Elements inside a free-text prompt. They live in
// the string model returned by VybyMentionInput, and are expanded at send-time
// into final prompt text + workflow image inputs.
//
// Format: {{element:<id>}}   where <id> matches [A-Za-z0-9_-]+
// ---------------------------------------------------------------------------

export const ELEMENT_TOKEN_PATTERN = '\\{\\{element:([A-Za-z0-9_-]+)\\}\\}'

export function makeElementTokenRegex() {
  return new RegExp(ELEMENT_TOKEN_PATTERN, 'g')
}

export function parseElementTokens(raw) {
  const str = `${raw || ''}`
  const tokens = []
  const regex = makeElementTokenRegex()
  let match = regex.exec(str)
  while (match) {
    tokens.push({
      start: match.index,
      end: match.index + match[0].length,
      id: match[1],
      token: match[0],
    })
    match = regex.exec(str)
  }
  return tokens
}

export function collectReferencedElementIds(raw) {
  const seen = new Set()
  const out = []
  parseElementTokens(raw).forEach((t) => {
    if (seen.has(t.id)) return
    seen.add(t.id)
    out.push(t.id)
  })
  return out
}

export function stripElementTokens(raw, elementsById = null) {
  const str = `${raw || ''}`
  const regex = makeElementTokenRegex()
  if (!elementsById) return str.replace(regex, '')
  return str.replace(regex, (_full, id) => {
    const el = elementsById[id]
    if (el?.kind === 'mood') return ''
    return el && el.name ? `@${el.name}` : ''
  })
}

// Descriptive phrase used when replacing a visual element token in the final
// prompt. Keeps intent-clarity without overwhelming the model; the attached
// reference image in the workflow slot carries the heavy lifting.
function descriptorForElement(element) {
  if (!element) return ''
  const name = `${element.name || ''}`.trim()
  if (!name) return ''
  if (element.kind === 'character') return name
  if (element.kind === 'location') return name
  if (element.kind === 'prop') return name
  return name
}

function indexElementsById(elements) {
  const out = Object.create(null)
  const list = Array.isArray(elements) ? elements : []
  list.forEach((el) => {
    if (el && el.id) out[el.id] = el
  })
  return out
}

// Expand a raw token-laden prompt into send-ready pieces.
//
// Returns:
//   {
//     text: string,                 // prompt with tokens replaced in-place
//                                   //   (visual → element name; personality → personality.prompt;
//                                   //    mood/unknown → removed)
//     personalityPrefix: string,    // RESERVED for the standalone Personality
//                                   //   chip (Phase 2). Inline @personality
//                                   //   chips no longer contribute to this —
//                                   //   they're spliced into `text` directly.
//     referencedElements: Element[] // unique, in first-seen order
//     referencedImages: Array<{elementId, kind, name, paths: string[]}>   // visual kinds only, active images only
//   }
export function expandPrompt({ raw, elements, maxElements = MAX_ELEMENTS_PER_PROMPT } = {}) {
  const str = `${raw || ''}`
  const byId = indexElementsById(elements)
  const tokens = parseElementTokens(str)

  const seen = new Set()
  const referencedElements = []
  const referencedImages = []

  tokens.forEach((tok) => {
    if (seen.has(tok.id)) return
    const el = byId[tok.id]
    if (!el) return
    // Mood is a standalone node-chip binding. If a stale/manual token exists,
    // remove it during text replacement but do not count it as a prompt chip.
    if (el.kind === 'mood') return
    if (referencedElements.length >= maxElements) return
    seen.add(tok.id)
    referencedElements.push(el)

    if (el.kind === 'personality') {
      // Personality text is now inlined into `text` (see replace below); we
      // still record the reference for caller telemetry / chip-cap accounting.
      return
    }
    if (VISUAL_ELEMENT_KINDS.has(el.kind)) {
      const paths = getElementActiveImagePaths(el)
      if (paths.length) {
        referencedImages.push({
          elementId: el.id,
          kind: el.kind,
          name: el.name || '',
          paths,
        })
      }
    }
  })

  // Replace tokens in text:
  //   - visual kind → element name (descriptor)
  //   - personality → personality.prompt text spliced literally at the token
  //                   position (in-place). Empty/missing prompt → empty.
  //   - mood/unknown id → removed
  let text = str.replace(makeElementTokenRegex(), (_full, id) => {
    const el = byId[id]
    if (!el) return ''
    if (el.kind === 'mood') return ''
    if (el.kind === 'personality') return `${el.prompt || ''}`.trim()
    return descriptorForElement(el)
  })

  // Collapse doubled whitespace that can result from inserted/removed tokens.
  text = text.replace(/[\t ]{2,}/g, ' ').replace(/[\t ]+\n/g, '\n').trim()

  // personalityPrefix is reserved for Phase 2 (standalone Personality chip).
  // For Phase 1 it is always empty — inline @personality chips contribute to
  // `text` instead. Kept on the return shape so call sites stay stable.
  const personalityPrefix = ''

  return {
    text,
    personalityPrefix,
    referencedElements,
    referencedImages,
  }
}

// Compose the final prompt string.
//
// As of Phase 1, `expanded.personalityPrefix` is always empty for inline
// @personality chips (they're spliced into `expanded.text` directly). The
// prefix path remains here for Phase 2: the standalone Personality chip's
// system-prompt binding will populate `personalityPrefix` upstream and this
// function will prepend it as before — `<system prompt>\n\n<user prompt>`.
export function composeExpandedPrompt(expanded, { allowPersonalityOnly = true } = {}) {
  const prefix = `${expanded?.personalityPrefix || ''}`.trim()
  const text = `${expanded?.text || ''}`.trim()
  if (!prefix) return text
  if (!text) return allowPersonalityOnly ? prefix : ''
  return `${prefix}\n\n${text}`
}

// Given the expansion's referencedImages and the workflow's schema, mutate
// `inputs` to populate empty image slots in order without clobbering values the
// user already set (drag-drop, "Use this Frame/Clip", etc).
//
// Strategy:
//   1. Discover image-capable slots in workflow.inputs (types: image/images).
//   2. For each referencedImages entry (first-seen order), its paths fill:
//      - `images` (list) slots → appended until full
//      - `image` (single) slots → only if currently empty
//   3. Only active image paths (already filtered in expandPrompt) are injected.
//
// Returns { injected: [{code, pathsAdded}], skippedImages: [{elementId, reason}] }
export function injectElementImagesIntoInputs(inputs, workflow, referencedImages) {
  const out = { injected: [], skippedImages: [] }
  if (!inputs || typeof inputs !== 'object') return out
  if (!workflow || !workflow.inputs || typeof workflow.inputs !== 'object') return out
  const refs = Array.isArray(referencedImages) ? referencedImages : []
  if (!refs.length) return out

  const schema = workflow.inputs
  const singleSlots = [] // { code, limit: 1 }
  const listSlots = [] // { code, limit: Infinity }

  Object.keys(schema).forEach((code) => {
    const inp = schema[code] || {}
    const t = `${inp.type || ''}`.toLowerCase()
    if (t === 'image') singleSlots.push(code)
    else if (t === 'images') listSlots.push(code)
  })

  if (!singleSlots.length && !listSlots.length) {
    refs.forEach((r) =>
      out.skippedImages.push({ elementId: r.elementId, reason: 'no_image_slot' }),
    )
    return out
  }

  // Flatten all referenced paths in order (respecting referenced order).
  const queue = []
  refs.forEach((r) => {
    r.paths.forEach((p) => queue.push({ elementId: r.elementId, path: p }))
  })

  // 1) Fill empty single slots first (one path each).
  for (const code of singleSlots) {
    if (!queue.length) break
    const cur = typeof inputs[code] === 'string' ? inputs[code] : ''
    if (cur && `${cur}`.trim()) continue
    const next = queue.shift()
    inputs[code] = next.path
    out.injected.push({ code, mode: 'single', pathsAdded: [next.path], elementId: next.elementId })
  }

  // 2) Append remainder into list slots.
  if (queue.length && listSlots.length) {
    const code = listSlots[0]
    const existing = Array.isArray(inputs[code]) ? [...inputs[code]] : []
    const pathsAdded = []
    while (queue.length) {
      const next = queue.shift()
      existing.push(next.path)
      pathsAdded.push(next.path)
    }
    inputs[code] = existing
    if (pathsAdded.length) {
      out.injected.push({ code, mode: 'list', pathsAdded })
    }
  } else if (queue.length) {
    while (queue.length) {
      const n = queue.shift()
      out.skippedImages.push({ elementId: n.elementId, reason: 'no_capacity' })
    }
  }

  return out
}

export function normalizeAiElements(list) {
  if (!Array.isArray(list)) return []

  const seen = new Set()
  return list.reduce((acc, item, index) => {
    const next = normalizeElement(item, index)
    if (!next || seen.has(next.id)) return acc
    seen.add(next.id)
    acc.push(next)
    return acc
  }, [])
}

export function groupElementsByKind(list) {
  const out = ELEMENT_KINDS.reduce((acc, k) => {
    acc[k] = []
    return acc
  }, {})
  if (!Array.isArray(list)) return out
  list.forEach((el) => {
    if (el && out[el.kind]) out[el.kind].push(el)
  })
  return out
}

// Group personalities (only) by their category. Used by the standalone
// Personality chip's 2-column dropdown in Phase 2; exposed here so the chip
// and the editor pane share one normalization path.
export function groupPersonalitiesByCategory(list) {
  const out = PERSONALITY_CATEGORIES.reduce((acc, c) => {
    acc[c] = []
    return acc
  }, {})
  if (!Array.isArray(list)) return out
  list.forEach((el) => {
    if (!el || el.kind !== 'personality') return
    const categories = normalizePersonalityCategories(el.categories || el.category)
    categories.forEach((c) => {
      if (out[c]) out[c].push(el)
    })
  })
  return out
}

export function groupMoodsByCategory(list) {
  const out = MOOD_CATEGORIES.reduce((acc, c) => {
    acc[c] = []
    return acc
  }, {})
  if (!Array.isArray(list)) return out
  list.forEach((el) => {
    if (!el || el.kind !== 'mood') return
    const c = normalizeMoodCategory(el.category)
    if (out[c]) out[c].push(el)
  })
  return out
}
